'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import CardProduct from 'components/cards/CardProduct';
import ButtonCTA from 'components/ui/ButtonCTA';
import ButtonCircle from 'components/ui/ButtonCircle';
import Text from 'components/ui/Text';

import { blockRootProps, getColor } from 'utils';

import styles from './BlockSubcategoryProductSlider.module.scss';

const BlockSubcategoryProductSlider = ({
    __typename,
    description,
    backgroundColor,
    cardHoverColor,
    subcategory,
    isNew,
    cta,
    products,
}) => {
    const scrollRef = useRef(null);
    const [hasMounted, setHasMounted] = useState(false);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);
    const title = subcategory;

    const updateScrollState = useCallback(() => {
        if (!scrollRef.current) return;
        const el = scrollRef.current;
        setIsBeginning(Math.floor(el.scrollLeft) === 0);
        setIsEnd(Math.ceil(el.scrollLeft + el.clientWidth) >= el.scrollWidth);
    }, []);

    useEffect(() => {
        if (!scrollRef.current) return;
        const el = scrollRef.current;
        setHasMounted(true);
        updateScrollState();
        el.addEventListener('scroll', updateScrollState);
        return () => el.removeEventListener('scroll', updateScrollState);
    }, [updateScrollState]);

    const getSlideWidth = useCallback(() => {
        const card = scrollRef.current?.querySelector(`.${styles.productCard}`);
        return card ? card.offsetWidth + 12 : 300; // card width + gap
    }, []);

    const handleNavigation = useCallback(
        direction => {
            scrollRef.current?.scrollBy({
                left: getSlideWidth() * (direction === 'next' ? 1 : -1),
                behavior: 'smooth',
            });
        },
        [getSlideWidth]
    );

    if (!products || products.length === 0) {
        return null;
    }

    // not relevant for mobile where we have fewer visible cards (buttons are hidden there)
    const hasOverflow = products.length > 3;

    return (
        <section
            {...blockRootProps(null, __typename)}
            className={styles.row}
            style={{
                '--row-bg-color': backgroundColor
                    ? getColor(backgroundColor)
                    : undefined,
            }}
        >
            <div className={styles.inner}>
                <div className={styles.sidebar}>
                    {isNew && (
                        <Text
                            baseTheme="labelSmall"
                            className={styles.newBadge}
                        >
                            NEW!
                        </Text>
                    )}
                    {title && (
                        <Text
                            as="h2"
                            baseTheme="displaySmall"
                            className={styles.title}
                        >
                            {title}
                        </Text>
                    )}
                    {description && (
                        <Text
                            baseTheme="bodyMedium"
                            className={styles.description}
                        >
                            {description}
                        </Text>
                    )}
                    {cta && <ButtonCTA className={styles.cta} {...cta} />}
                </div>
                <div className={styles.productsWrapper}>
                    <div className={styles.productsContainer}>
                        <div ref={scrollRef} className={styles.productsScroll}>
                            {products.filter(Boolean).map((product, i) => (
                                <div
                                    key={`${product.sys?.id || 'product'}-${i}`}
                                    className={styles.productCard}
                                >
                                    <CardProduct
                                        {...product}
                                        id={product.sys?.id}
                                        hoverColorOverride={cardHoverColor}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    {hasMounted && hasOverflow && (
                        <div className={styles.navigation}>
                            <ButtonCircle
                                className={styles.prevButton}
                                theme="small-fill-blue"
                                iconType="chevronLeft"
                                onClick={() => handleNavigation('prev')}
                                disabled={isBeginning}
                            />
                            <ButtonCircle
                                className={styles.nextButton}
                                theme="small-fill-blue"
                                iconType="chevronRight"
                                onClick={() => handleNavigation('next')}
                                disabled={isEnd}
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

BlockSubcategoryProductSlider.propTypes = {
    __typename: PropTypes.string,
    description: PropTypes.string,
    backgroundColor: PropTypes.string,
    subcategory: PropTypes.string,
    isNew: PropTypes.bool,
    cta: PropTypes.object,
    products: PropTypes.arrayOf(PropTypes.object),
};

export default BlockSubcategoryProductSlider;
